import { Business } from '@/types/business';
import { BusinessWire } from '../schemas/business';

/**
 * Mapping functions to convert wire format (backend) to domain format (app)
 */

/**
 * Convert wire business format to domain business format
 * Handles type conversions: ISO strings -> Unix timestamps, null -> undefined
 */
export function mapBusinessWireToDomain(wire: BusinessWire): Business {
  // Convert ISO datetime strings to Unix timestamps (milliseconds)
  const createdAt = wire.created_at ? new Date(wire.created_at).getTime() : Date.now();
  const updatedAt = wire.updated_at ? new Date(wire.updated_at).getTime() : Date.now();

  return {
    id: wire.id,
    name: wire.name,
    description: wire.description || undefined, // Convert null to undefined
    status: wire.status,
    ownerId: wire.owner_id || undefined,
    latitude: wire.latitude ?? undefined, // Preserve 0 as valid value
    longitude: wire.longitude ?? undefined,
    address: wire.address || undefined, // D1 legacy field
    street: wire.street || undefined,
    city: wire.city || undefined,
    state: wire.state || undefined,
    country: wire.country || undefined,
    postalCode: wire.postal_code || undefined,
    timezone: wire.timezone || undefined,
    phone: wire.phone || undefined,
    email: wire.email || undefined,
    website: wire.website || undefined,
    category: wire.category || undefined, // D1 legacy field
    cuisineType: wire.cuisine_type || undefined,
    priceLevel: wire.price_level || undefined,
    hours: wire.hours || undefined,
    tags: wire.tags || undefined,
    coverPhotoUrl: wire.cover_photo_url || undefined,
    rating: wire.rating ?? undefined,
    totalRatings: wire.total_ratings
      || (wire.rating_distribution
        ? wire.rating_distribution.not_for_me
          + wire.rating_distribution.okay
          + wire.rating_distribution.try_it
          + wire.rating_distribution.must_try
        : 0),
    ratingDistribution: wire.rating_distribution ? {
      notForMe: wire.rating_distribution.not_for_me,
      okay: wire.rating_distribution.okay,
      tryIt: wire.rating_distribution.try_it,
      mustTry: wire.rating_distribution.must_try,
    } : undefined,
    createdAt,
    updatedAt,
    // Source/provenance fields
    osmId: wire.osm_id || undefined,
    osmType: wire.osm_type || undefined,
    osmFullId: wire.osm_full_id || undefined,
    geoapifyId: wire.geoapify_id || undefined,
    sourceDataSources: wire.source_data_sources || undefined,
    sourceLastUpdated: wire.source_last_updated || undefined,
    sourceVerifiedBy: wire.source_verified_by || undefined,
    sourceNotes: wire.source_notes || undefined,
  };
}

/**
 * Convert domain business format to wire format (for requests)
 */
export function mapBusinessDomainToWire(domain: Partial<Business>): Partial<BusinessWire> {
  const wire: Partial<BusinessWire> = {};

  if (domain.id !== undefined) wire.id = domain.id;
  if (domain.name !== undefined) wire.name = domain.name;
  if (domain.description !== undefined) wire.description = domain.description;
  if (domain.status !== undefined) wire.status = domain.status;
  if (domain.ownerId !== undefined) wire.owner_id = domain.ownerId;
  if (domain.latitude !== undefined) wire.latitude = domain.latitude;
  if (domain.longitude !== undefined) wire.longitude = domain.longitude;
  if (domain.address !== undefined) wire.address = domain.address;
  if (domain.street !== undefined) wire.street = domain.street;
  if (domain.city !== undefined) wire.city = domain.city;
  if (domain.state !== undefined) wire.state = domain.state;
  if (domain.country !== undefined) wire.country = domain.country;
  if (domain.postalCode !== undefined) wire.postal_code = domain.postalCode;
  if (domain.timezone !== undefined) wire.timezone = domain.timezone;
  if (domain.phone !== undefined) wire.phone = domain.phone;
  if (domain.email !== undefined) wire.email = domain.email;
  if (domain.website !== undefined) wire.website = domain.website;
  if (domain.category !== undefined) wire.category = domain.category;
  if (domain.cuisineType !== undefined) wire.cuisine_type = domain.cuisineType;
  if (domain.priceLevel !== undefined) wire.price_level = domain.priceLevel;
  if (domain.hours !== undefined) wire.hours = domain.hours;
  if (domain.tags !== undefined) wire.tags = domain.tags;
  if (domain.coverPhotoUrl !== undefined) wire.cover_photo_url = domain.coverPhotoUrl;
  if (domain.rating !== undefined) wire.rating = domain.rating;
  if (domain.totalRatings !== undefined) wire.total_ratings = domain.totalRatings;
  if (domain.ratingDistribution !== undefined) wire.rating_distribution = domain.ratingDistribution ? {
    not_for_me: domain.ratingDistribution.notForMe,
    okay: domain.ratingDistribution.okay,
    try_it: domain.ratingDistribution.tryIt,
    must_try: domain.ratingDistribution.mustTry,
  } : null;
  if (domain.createdAt !== undefined) wire.created_at = new Date(domain.createdAt).toISOString();
  if (domain.updatedAt !== undefined) wire.updated_at = new Date(domain.updatedAt).toISOString();
  // Source/provenance fields
  if (domain.osmId !== undefined) wire.osm_id = domain.osmId;
  if (domain.osmType !== undefined) wire.osm_type = domain.osmType;
  if (domain.osmFullId !== undefined) wire.osm_full_id = domain.osmFullId;
  if (domain.geoapifyId !== undefined) wire.geoapify_id = domain.geoapifyId;
  if (domain.sourceDataSources !== undefined) wire.source_data_sources = domain.sourceDataSources;
  if (domain.sourceLastUpdated !== undefined) wire.source_last_updated = domain.sourceLastUpdated;
  if (domain.sourceVerifiedBy !== undefined) wire.source_verified_by = domain.sourceVerifiedBy;
  if (domain.sourceNotes !== undefined) wire.source_notes = domain.sourceNotes;

  return wire;
}
